import { AntTooltip, Icons, defaultTheme } from '@agro-club/agroclub-shared'
import { Company, FactoringStatus } from 'modules/domain/company/types'
import { CompanyInDeal } from 'modules/domain/deal/types'
import { CompanyMembersTypes } from 'modules/domain/user/types'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { factoringStatusColors } from 'views/pages/Company/helpers'

type Props = {
  isVisible?: boolean
  text: string
  children?: React.ReactNode
  overlayStyle?: React.CSSProperties
}

type PropsIcon = {
  isVisible?: boolean
  size?: 'large' | 'small'
}

export const IconWrapper = styled.div<{ $iconColor?: string; size?: 'large' | 'small' }>`
  display: inline;
  width: ${({ size }) => (size === 'large' ? '24px' : '16px')};
  height: ${({ size }) => (size === 'large' ? '24px' : '16px')};

  svg {
    width: inherit;
    height: inherit;

    path {
      fill: ${({ $iconColor }) => $iconColor};
    }
  }
`

export const TooltipIcon: FC<Props> = ({ isVisible = false, text, children, overlayStyle }) => {
  if (!isVisible) return null

  return (
    <AntTooltip tooltipContent={text} variant="secondary" placement="top" overlayStyle={overlayStyle}>
      {children}
    </AntTooltip>
  )
}

export const KeyEntityIcon: FC<PropsIcon> = ({ isVisible, size }) => {
  const { t } = useTranslation('user')
  return (
    <TooltipIcon isVisible={isVisible} text={t('key_user')}>
      <IconWrapper $iconColor={defaultTheme.color.accentApprove600} size={size}>
        <Icons.IconDollar />
      </IconWrapper>
    </TooltipIcon>
  )
}

export const VerifiedUserIcon: FC<PropsIcon> = ({ isVisible, size }) => {
  const { t } = useTranslation('user')
  return (
    <TooltipIcon isVisible={isVisible} text={t('verify_user')}>
      <IconWrapper $iconColor={defaultTheme.color.primary600} size={size}>
        <Icons.IconCheckMark />
      </IconWrapper>
    </TooltipIcon>
  )
}

export const NotVerifiedUserIcon: FC<PropsIcon> = ({ isVisible, size }) => {
  const { t } = useTranslation('user')
  return (
    <TooltipIcon isVisible={isVisible} text={t('not_verify_user')}>
      <IconWrapper size={size}>
        <Icons.IconCheckMark />
      </IconWrapper>
    </TooltipIcon>
  )
}

export const BlockedUserIcon: FC<PropsIcon> = ({ isVisible, size }) => {
  const { t } = useTranslation('user')
  return (
    <TooltipIcon isVisible={isVisible} text={t('userBlocker')}>
      <IconWrapper size={size} $iconColor={defaultTheme.color.accentDestruct600}>
        <Icons.IconRemoved />
      </IconWrapper>
    </TooltipIcon>
  )
}

export const CoordinatorVerifiedUserIcon: FC<PropsIcon> = ({ isVisible, size }) => {
  const { t } = useTranslation('user')
  return (
    <TooltipIcon isVisible={isVisible} text={t('verify_coordinator')}>
      <IconWrapper $iconColor={defaultTheme.color.accentNotify600} size={size}>
        <Icons.IconCheckMark />
      </IconWrapper>
    </TooltipIcon>
  )
}

export const SecurityVerifiedUserIcon: FC<PropsIcon> = ({ isVisible, size }) => {
  const { t } = useTranslation('user')
  return (
    <TooltipIcon isVisible={isVisible} text={t('verify_security')}>
      <IconWrapper $iconColor={defaultTheme.color.accentNotify600} size={size}>
        <Icons.IconDefend />
      </IconWrapper>
    </TooltipIcon>
  )
}

export const KycVerifiedUserIcon: FC<PropsIcon> = ({ isVisible, size }) => {
  const { t } = useTranslation('user')
  return (
    <TooltipIcon isVisible={isVisible} text={t('verify_kyc')}>
      <IconWrapper $iconColor={defaultTheme.color.accentNotify600} size={size}>
        <Icons.IconCrops />
      </IconWrapper>
    </TooltipIcon>
  )
}

export const UserHasDealsIcon: FC<PropsIcon> = ({ isVisible, size }) => {
  const { t } = useTranslation('user')
  return (
    <TooltipIcon isVisible={isVisible} text={t('completed_transactions')}>
      <IconWrapper $iconColor={defaultTheme.color.primary600} size={size}>
        <Icons.IconDeals />
      </IconWrapper>
    </TooltipIcon>
  )
}

interface CompanyFactoringIconProps {
  company: Company | CompanyInDeal
  // look for specific factoring type
  profileType?: CompanyMembersTypes
}

export const CompanyFactoringIcon = ({ company, profileType }: CompanyFactoringIconProps) => {
  const { t } = useTranslation('company')
  let factoringStatus

  if (profileType === CompanyMembersTypes.seller) {
    if (company.factoring_seller_status === FactoringStatus.accepted) {
      factoringStatus = FactoringStatus.accepted
    } else if (company.factoring_seller_status === FactoringStatus.in_progress) {
      factoringStatus = FactoringStatus.in_progress
    }
  } else if (profileType === CompanyMembersTypes.customer) {
    if (company.factoring_customer_status === FactoringStatus.accepted) {
      factoringStatus = FactoringStatus.accepted
    } else if (company.factoring_customer_status === FactoringStatus.in_progress) {
      factoringStatus = FactoringStatus.in_progress
    }
  } else if (
    company.factoring_customer_status === FactoringStatus.accepted ||
    company.factoring_seller_status === FactoringStatus.accepted
  ) {
    factoringStatus = FactoringStatus.accepted
  } else if (
    company.factoring_customer_status === FactoringStatus.in_progress ||
    company.factoring_seller_status === FactoringStatus.in_progress
  ) {
    factoringStatus = FactoringStatus.in_progress
  }

  if (!factoringStatus) return null

  return (
    <TooltipIcon text={t(`factoringStatuses.${factoringStatus}`)} isVisible>
      <IconWrapper $iconColor={factoringStatusColors[factoringStatus]}>
        <Icons.IconFactoring />
      </IconWrapper>
    </TooltipIcon>
  )
}

export const USCompanyIrelyIcon: FC<PropsIcon> = ({ isVisible, size }) => {
  const { t } = useTranslation('company')
  return (
    <TooltipIcon isVisible={isVisible} text={t('irelySync')}>
      <IconWrapper $iconColor={defaultTheme.color.accentCyan600} size={size}>
        <Icons.IconIrely />
      </IconWrapper>
    </TooltipIcon>
  )
}

export const CommmentIcon: FC<{ comment: string }> = ({ comment }) => {
  return (
    <TooltipIcon isVisible text={comment} overlayStyle={{ maxWidth: '550px' }}>
      <IconWrapper $iconColor={defaultTheme.color.primary600} size="large">
        <Icons.IconInfoCircle />
      </IconWrapper>
    </TooltipIcon>
  )
}
